import { useState, useEffect } from 'react';
import Reveal from './Reveal';
import FlipNumber from './FlipNumber';

export default function SoundPhilosophySection() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const approaches = [
    {
      icon: 'ri-heart-pulse-line',
      title: '感情を動かす音',
      description: '音楽・SE・ナレーションが融合することで、映像に"命"が吹き込まれます。WakuWakuは、その一瞬を一緒に設計します。',
    },
    {
      icon: 'ri-equalizer-line',
      title: '緻密なMA（マルチトラック編集）',
      description: '28年以上の現場経験をもとに、セリフ・効果音・音楽の3要素を精緻にバランス調整。視聴者が「自然に」作品に没入できる音を追求します。',
    },
    {
      icon: 'ri-robot-line',
      title: 'AIと職人技の融合',
      description: '最新のAIナレーション技術と、熟練エンジニアの感性を掛け合わせることで、スピードと品質を同時に実現します。',
    },
    {
      icon: 'ri-broadcast-line',
      title: '現場に寄り添う柔軟性',
      description: 'スタジオ内に留まらず、ロケーション録音・出張編集にも対応。クライアントの現場にて、即戦力として機能します。',
    },
  ];

  return (
    <section id="sound-philosophy" className="py-28 md:py-36 bg-site-dark relative overflow-hidden">
      {/* 背景装飾 */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-teal-500/10 rounded-full -translate-x-1/2 -translate-y-1/2 opacity-60"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-teal-500/10 rounded-full translate-x-1/3 translate-y-1/3 opacity-60"></div>
      </div>

      <div className="container mx-auto px-4 relative">
        <div className="max-w-6xl mx-auto">

          {/* セクションタイトル */}
          <Reveal>
          <div className="text-center mb-20">
            <p className="eyebrow text-teal-400 text-sm md:text-base mb-3">Sound Philosophy</p>
            <div className="inline-flex items-center gap-2 bg-teal-500/10 text-teal-300 text-sm font-semibold px-4 py-1.5 rounded-full mb-6 border border-teal-400/20">
              <i className="ri-sparkling-line"></i>
              <span>2026年3月24日 更新</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-mincho text-white mb-5">
              合同会社 WakuWakuの<br className="md:hidden" />
              <span className="text-teal-400">「音」</span>への取り組み
            </h2>
            <div className="w-20 h-[3px] bg-teal-400 mx-auto mb-6 rounded-full"></div>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto leading-relaxed">
              音は、映像に感情を吹き込む最後のピース。<br />
              私たちは「音のデザイン」を通して、観る人の心を揺さぶる体験をつくります。
            </p>
          </div>
          </Reveal>

          {/* メインコンテンツ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center mb-20">
            {/* 画像エリア */}
            <Reveal variant="fly" direction="left">
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden border border-white/10">
                <img
                  src="/images/brand/sound-approach.jpg"
                  alt="WakuWaku 音への取り組み"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
              {/* フローティングバッジ */}
              <div className="absolute -bottom-5 -right-5 bg-teal-500 text-white rounded-2xl px-5 py-4 text-center shadow-lg shadow-teal-500/20">
                <FlipNumber value={28} suffix="+" duration={1800} triggerOnView className="text-2xl font-light" />
                <div className="text-xs text-teal-100 whitespace-nowrap">年の現場経験</div>
              </div>
            </div>
            </Reveal>

            {/* テキストエリア */}
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl md:text-3xl font-mincho text-white mb-4">
                  <span className="text-teal-400 mr-2">「音」</span>は感情のスイッチ
                </h3>
                <p className="text-zinc-400 leading-relaxed text-base mb-4">
                  映像は目で「観る」ものですが、人の心が動くのは「音」が鍵を握っています。
                  台詞の温度感、BGMの空気感、効果音の存在感——すべてが重なったとき、はじめて作品は"体験"になります。
                </p>
                <p className="text-zinc-400 leading-relaxed text-base mb-4">
                  WakuWakuは、その「音のデザイン」を担う専門チームです。
                  企業VP・CM・映画・展示会・縦型ドラマまで、多様なフォーマットに対応しながら、
                  クライアントのビジョンを音で具現化します。
                </p>
                <p className="text-zinc-400 leading-relaxed text-base">
                  私たちが大切にしているのは、技術だけではありません。
                  「この音を聴いた人が、どう感じるか」を常に想像しながら、
                  <strong className="text-teal-300">人をWakuWakuさせる音</strong>を一緒に創り続けます。
                </p>
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                {['MA / ミックス', 'SE制作', 'AIナレーション', 'ロケーション録音', '出張編集', '展示会音響'].map((tag) => (
                  <span
                    key={tag}
                    className="bg-teal-500/10 text-teal-300 text-sm px-4 py-1.5 rounded-full border border-teal-400/20 font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* 4つのアプローチカード */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {approaches.map((item, index) => (
              <div
                key={index}
                className={`bg-white/[0.03] rounded-xl p-6 border border-white/10 hover:border-teal-400/40 hover:bg-teal-500/5 transition-all cursor-default group ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: `${index * 80}ms`, transition: 'all 0.5s ease, opacity 0.5s ease, transform 0.5s ease' }}
              >
                <div className="w-12 h-12 flex items-center justify-center bg-white/5 rounded-lg mb-4 group-hover:bg-teal-500 transition-colors border border-white/10">
                  <i className={`${item.icon} text-xl text-teal-300 group-hover:text-white transition-colors`}></i>
                </div>
                <h4 className="text-base font-medium text-white mb-2">{item.title}</h4>
                <p className="text-zinc-400 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}